<template>
    <div>
        <ResourcesActionItemsBlock four-cols :items="items" :minified="minified" />
    </div>
</template>

<script setup lang="ts">
import { MentoringCategoriesMapped, MentoringCategory } from '#shared/types/Mentoring'
import type { Project } from '#shared/types/Project'
import { ProjectType } from '#shared/types/Project'
import { getAvailableVolunteeringWeekSpots } from '#shared/utils/volunteeringWeekCapacity'
import type { ResourcesActionItemsBlockType } from '@/components/common/ResourcesActionItemsBlock.vue'
import ResourcesActionItemsBlock from '@/components/common/ResourcesActionItemsBlock.vue'
import { formatSlovakRegionLabel } from '@/utils/slovakRegion'
import {
    getVolunteeringWeekOpportunityFullDateLabel,
    getVolunteeringWeekOpportunityTimeRangeLabel
} from '@/utils/volunteeringWeekOpportunity'
import groupBy from 'lodash-es/groupBy'
import mapValues from 'lodash-es/mapValues'
import type { PropType } from 'vue'
import { computed } from 'vue'

const props = defineProps({
    project: {
        type: Object as PropType<Project>,
        required: true
    },
    minified: {
        type: Boolean,
        default: false
    }
})

const projectCooperationHow = computed(() => {
    return (props.project.how || [])
        .map((how) => valueLookupById(how, MentoringCategoriesMapped[MentoringCategory.How]))
        .join(', ')
})

const projectLengthMinimumTime = computed(() => {
    return projectDurationItem(props.project.duration?.[0])
})

const hasDuration = computed(() => {
    return props.project.duration?.length > 0
})

const hasHow = computed(() => {
    return props.project.how?.length > 0
})

const isVolunteeringWeekProject = computed(() => props.project.type === ProjectType.VOLUNTEERING_WEEK)

const volunteeringWeekDateLabel = computed(() => getVolunteeringWeekOpportunityFullDateLabel(props.project))

const volunteeringWeekTimeRangeLabel = computed(() => getVolunteeringWeekOpportunityTimeRangeLabel(props.project))

const locationFormatted = computed(() => {
    const region = formatSlovakRegionLabel(props.project.geoRegion)
    const city = props.project.geoCity
    return [region, city].filter(Boolean).join(', ')
})

const availableSpots = computed(() => {
    return getAvailableVolunteeringWeekSpots(props.project)
})

const items = computed<ResourcesActionItemsBlockType>(() => {
    const nextItems: ResourcesActionItemsBlockType = []

    if (locationFormatted.value) {
        nextItems.push({
            title: 'Miesto',
            icon: 'place-48',
            subTitle: locationFormatted.value
        })
    }

    if (isVolunteeringWeekProject.value) {
        nextItems.push({
            title: 'Kedy',
            icon: 'calendar-48',
            subTitle: volunteeringWeekDateLabel.value,
            secondaryRows: volunteeringWeekTimeRangeLabel.value
                ? [{ text: volunteeringWeekTimeRangeLabel.value }]
                : undefined
        })
    } else if (props.project.when) {
        nextItems.push({
            title: 'Kedy',
            icon: 'calendar-48',
            subTitle: props.project.when,
            maxLength: 30
        })
    }

    if (hasHow.value) {
        const howString = projectCooperationHow.value
        nextItems.push({
            title: 'Spôsob spolupráce',
            icon: 'cooperation-48',
            subTitle: howString.charAt(0).toUpperCase() + howString.slice(1)
        })
    }

    if (hasDuration.value && !isVolunteeringWeekProject.value) {
        nextItems.push({
            title: 'Dĺžka projektu',
            icon: 'duration-48',
            subTitle: projectLengthMinimumTime.value
        })
    }

    if (props.project.type === ProjectType.VOLUNTEERING_WEEK) {
        nextItems.push({
            title: 'Voľné miesta',
            icon: 'group',
            subTitle: `${availableSpots.value ?? 0} (z ${props.project.maxVolunteerCount ?? 0})`
        })
    }

    return nextItems
})

function projectDurationItem(item?: string) {
    return valueLookupById(item, MentoringCategoriesMapped[MentoringCategory.DurationProject])
}

function mapValuesStr(optionsList?: any[]) {
    if (!optionsList) {
        return {}
    }
    return mapValues(groupBy(optionsList, 'id'), (x) => x[0])
}

function valueLookupById(item?: string, list?: any[]) {
    if (list && item) {
        const option = mapValuesStr(list)
        if (option && option[item]) {
            const result = option[item]
            if ('label' in result) {
                return result.label
            }
        }
    }
    return ''
}
</script>
